import React, { useEffect, useMemo, useState } from "react";
import { Text } from "@chakra-ui/react";

import { fetchTrades } from "../../../services/tradeApi";
import { backendTradeToFrontendTrade } from "../../../services/tradeStorage";
import type { Backtest } from "../../../types/backtests";
import type { Trade } from "../../../types/trade";
import { dedupeBacktestsById } from "../../../services/backtestsApi";
import { isTradeManagementBacktest } from "../../../utils/backtestSimulationOverlay";
import {
  formatDateAsCalendarDayInTimeZone,
} from "../../../utils/notificationPeriod";
import { resolveReportTradeReviewFetchWindow } from "../../../utils/reportTradeReviewWindow";
import { filterTradesExitedOnOrBefore } from "../../../utils/reportTradingPnlSeries";
import { ReportTradingPnlSection } from "../Reports/components/ReportTradingPnlSection";
import {
  comparisonScopeReviewFingerprint,
  getComparisonRunSavedTitle,
  type ComparisonRun,
} from "./simulatorComparisonTypes";
import { useSimulatorComparisonRuns } from "./simulatorComparisonContext";
import { SimulatorAlert } from "./SimulatorAlert";

function parseCalendarYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return new Date(y, mo - 1, d, 0, 0, 0, 0);
}

function scopeToDateRange(scope: ComparisonRun["scope"]): {
  startDate: Date | null;
  endDate: Date | null;
} {
  if (scope.localStartDate && scope.localEndDate) {
    return {
      startDate: parseCalendarYmd(scope.localStartDate),
      endDate: parseCalendarYmd(scope.localEndDate),
    };
  }
  return { startDate: null, endDate: null };
}

function runHasReviewableResult(run: ComparisonRun): boolean {
  return run.status !== "failed" && !run.error && (run.status === "succeeded" || run.result != null);
}

export type SimulatorTradingPnlSectionProps = {
  brokerAccountCode: string;
  reportingTimeZone: string;
  subtleText: string;
  reportId?: string | null;
  reportCreatedAt?: string | null;
  prefillWindow?: {
    start_date?: string | null;
    end_date?: string | null;
    timeframe?: string | null;
  } | null;
  linkedBacktests?: Backtest[];
};

/**
 * Trading PnL card for simulator / workspace results (same chart as report detail).
 */
export function SimulatorTradingPnlSection(
  props: SimulatorTradingPnlSectionProps
): React.ReactElement | null {
  const { comparisonRuns, selectedRun } = useSimulatorComparisonRuns();
  const {
    brokerAccountCode,
    reportingTimeZone,
    subtleText,
    reportId,
    reportCreatedAt,
    prefillWindow,
    linkedBacktests: linkedBacktestsProp = [],
  } = props;

  const scopeSourceRun = useMemo(() => {
    const reviewable = comparisonRuns.find(runHasReviewableResult);
    return reviewable ?? selectedRun ?? comparisonRuns[0] ?? null;
  }, [comparisonRuns, selectedRun]);

  const scopeFingerprint = useMemo(
    () => (scopeSourceRun ? comparisonScopeReviewFingerprint(scopeSourceRun.scope) : ""),
    [scopeSourceRun]
  );

  const reportCreatedAsOfExactMs = useMemo(() => {
    const raw = reportCreatedAt?.trim();
    if (!raw) return null;
    const ms = Date.parse(raw);
    return Number.isFinite(ms) ? ms : null;
  }, [reportCreatedAt]);

  const reportCreatedAsOfYmd = useMemo(() => {
    if (reportCreatedAsOfExactMs == null) return null;
    return formatDateAsCalendarDayInTimeZone(
      new Date(reportCreatedAsOfExactMs),
      reportingTimeZone
    );
  }, [reportCreatedAsOfExactMs, reportingTimeZone]);

  const reportCreatedAsOfLabel = reportCreatedAsOfYmd;

  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(false);
  const [tradesError, setTradesError] = useState<string | null>(null);

  const dateRangeForFetch = useMemo(
    () => (scopeSourceRun ? scopeToDateRange(scopeSourceRun.scope) : { startDate: null, endDate: null }),
    [scopeSourceRun]
  );
  const timeframeForFetch = scopeSourceRun?.scope?.timeframe ?? prefillWindow?.timeframe ?? "all-time";
  const scopeSymbol = scopeSourceRun?.scope?.symbol?.trim() ?? "";

  useEffect(() => {
    if (!scopeSourceRun || !brokerAccountCode.trim() || !runHasReviewableResult(scopeSourceRun)) {
      setTrades([]);
      setTradesError(null);
      setTradesLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      setTradesLoading(true);
      setTradesError(null);
      try {
        const win = resolveReportTradeReviewFetchWindow({
          dateRange: dateRangeForFetch,
          timeframe: timeframeForFetch,
          reportingTimeZone,
          asOfYmd: reportCreatedAsOfYmd,
        });
        const opts: Parameters<typeof fetchTrades>[0] = {
          broker_account_code: brokerAccountCode,
          status: "closed",
          cache_bust: Date.now(),
        };
        if (scopeSymbol) opts.symbol = scopeSymbol;
        if (win.kind === "local") {
          opts.local_period = win.period;
        } else {
          opts.timeframe = win.timeframe;
        }
        const response = await fetchTrades(opts);
        if (cancelled) return;
        const list = Array.isArray(response?.trades)
          ? filterTradesExitedOnOrBefore(
              response.trades.map(backendTradeToFrontendTrade),
              reportCreatedAsOfExactMs
            )
          : [];
        setTrades(list);
      } catch (e) {
        if (cancelled) return;
        setTrades([]);
        setTradesError(e instanceof Error ? e.message : "Failed to load trades for Trading PnL.");
      } finally {
        if (!cancelled) setTradesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    brokerAccountCode,
    dateRangeForFetch,
    reportingTimeZone,
    reportCreatedAsOfExactMs,
    reportCreatedAsOfYmd,
    scopeFingerprint,
    scopeSourceRun,
    scopeSymbol,
    timeframeForFetch,
  ]);

  const linkedBacktests = useMemo(() => {
    const byId = new Map<string, Backtest>();
    for (const b of dedupeBacktestsById(linkedBacktestsProp)) {
      if (b?.id) byId.set(b.id, b);
    }
    for (const run of comparisonRuns) {
      const id = run.backtestId?.trim();
      if (!id) continue;
      const existing = byId.get(id);
      if (existing?.result != null) continue;
      byId.set(id, {
        ...(existing ?? {}),
        id,
        title: getComparisonRunSavedTitle(run),
        result: run.result ?? existing?.result,
      } as Backtest);
    }
    return Array.from(byId.values());
  }, [comparisonRuns, linkedBacktestsProp]);

  const isTradeManagement = useMemo(
    () =>
      linkedBacktests.some((b) => isTradeManagementBacktest(b)) ||
      Boolean(selectedRun?.simulatedLegs?.length) ||
      Boolean(selectedRun?.result?.changed != null),
    [linkedBacktests, selectedRun]
  );

  if (!scopeSourceRun || !runHasReviewableResult(scopeSourceRun)) return null;

  if (!brokerAccountCode.trim()) {
    return (
      <SimulatorAlert tone="warning">
        Select a broker account in scope to load Trading PnL.
      </SimulatorAlert>
    );
  }

  if (tradesError) {
    return (
      <Text fontSize="sm" color={subtleText} fontFamily="var(--font-primary)" mt={4}>
        {tradesError}
      </Text>
    );
  }

  return (
    <ReportTradingPnlSection
      reportId={reportId?.trim() || selectedRun?.backtestId || "simulator"}
      trades={trades}
      linkedBacktests={linkedBacktests}
      isTradeManagement={isTradeManagement}
      tradesLoading={tradesLoading}
      asOfEndMs={reportCreatedAsOfExactMs}
      asOfLabel={reportCreatedAsOfLabel}
    />
  );
}
